import {createSlice, PayloadAction} from '@reduxjs/toolkit'

import {DevicePlatform} from '@vio/core/src/_web/utils/userAgent/types'
import {BrandCode} from '@vio/core/src/brand/types'

import type {GhaClassification} from '@vio/core/src/rateChecker'
import type {DeviceCategory} from '@vio/core/src/utils/userAgent/types'

export interface MetaState {
  brandCode: BrandCode
  subBrand?: string
  anonymousId?: string
  userCountry?: string
  // [PICC] The trigger for the PICC offer check and UI: the card's issuing
  // country, set ONLY when the display currency is chargeable and the card's
  // currency differs from it (i.e. there is a choice worth offering).
  piccCardCountry?: string
  userCity?: string
  searchId: string
  devicePlatform?: DevicePlatform
  /** Server-computed GHA visitor classification */
  ghaClassification?: GhaClassification
  currency?: string
  locale?: string
  deviceType?: DeviceCategory
  queryString?: string
  isVioFlutter: boolean
  userAgent?: string
}

export const initialState: MetaState = {
  brandCode: undefined!, // root meta state is not strict
  subBrand: undefined,
  anonymousId: undefined,
  userCountry: undefined,
  piccCardCountry: undefined,
  userCity: undefined,
  ghaClassification: undefined,
  searchId: '',
  currency: undefined,
  locale: undefined,
  deviceType: undefined,
  queryString: undefined,
  isVioFlutter: false,
  userAgent: undefined
}

interface SetUserCountry {
  userCountry: string
}

const {actions, reducer} = createSlice({
  name: 'meta',
  initialState,
  reducers: {
    setUserCountry: (state, {payload}: PayloadAction<SetUserCountry>) => {
      state.userCountry = payload.userCountry
    },
    setPiccCardCountry: (
      state,
      {payload}: PayloadAction<{cardCountry: string}>
    ) => {
      state.piccCardCountry = payload.cardCountry
    },
    // [PICC] Retracts the currency choice while the card number is being
    // edited — the new number may be issued elsewhere, so the old choice is stale.
    clearPiccCardCountry: state => {
      state.piccCardCountry = undefined
    },
    setUserSelectedCurrency: (
      state,
      {payload}: PayloadAction<{currency: string}>
    ) => {
      state.currency = payload.currency
    },
    syncStoreWithUrl: (
      state,
      {payload}: PayloadAction<{queryString: string}>
    ) => {
      state.queryString = payload.queryString
    }
  }
})

export default reducer

export const {
  setUserCountry,
  setPiccCardCountry,
  clearPiccCardCountry,
  setUserSelectedCurrency,
  syncStoreWithUrl
} = actions
